import { api } from "@convex/_generated/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v3";

import { Button } from "~/components/ui/button";
import { Combobox, type ComboboxItem } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "~/components/ui/input-group";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

const getStripeTaxCodesRef = api.taxCategories.getStripeTaxCodes;

const createTaxCategoryRef = api.taxCategories.create;

// V4.1: Search params for pre-filling category name
const searchParamsSchema = z.object({
  name: z.string().optional(),
});

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/items/$itemId/create-tax-category"
)({
  component: CreateTaxCategoryModal,
  validateSearch: searchParamsSchema,
});

// Schema with conditional validation
const taxCategorySchema = z
  .object({
    name: z
      .string()
      .min(1, "Name is required")
      .max(100, "Name must be less than 100 characters"),
    taxMethod: z.enum(["automatic", "manual"]),
    value: z.string().optional(),
    taxCode: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.taxMethod === "manual") {
        if (!data.value || data.value.trim() === "") {
          return false;
        }
        const num = Number.parseFloat(data.value);
        return !Number.isNaN(num) && num >= 0 && num <= 100;
      }
      return !!data.taxCode && data.taxCode.trim() !== "";
    },
    {
      message:
        "Please provide a tax rate (manual) or select a Stripe tax code (automatic)",
      path: ["value"],
    }
  );

type TaxCategoryFormData = z.infer<typeof taxCategorySchema>;

function CreateTaxCategoryModal() {
  const { menuId, itemId } = Route.useParams();
  const { name } = Route.useSearch();
  const navigate = useNavigate();

  // Fetch Stripe tax codes for the dropdown
  const { data: stripeTaxCodes, isLoading: isLoadingTaxCodes } = useQuery({
    queryKey: ["stripeTaxCodes"],
    queryFn: () => {
      return convexClient.action(getStripeTaxCodesRef, {});
    },
    staleTime: 1000 * 60 * 60,
  });

  const taxCodeOptions: ComboboxItem<string>[] = (stripeTaxCodes ?? []).map(
    (code) => ({
      value: code.id,
      label: code.name,
      keywords: [code.id, code.description.toLowerCase()],
    })
  );

  const form = useForm<TaxCategoryFormData>({
    resolver: zodResolver(taxCategorySchema),
    defaultValues: {
      name: name ?? "",
      taxMethod: "manual",
      value: "",
      taxCode: "",
    },
  });

  const taxMethod = useWatch({ control: form.control, name: "taxMethod" });

  const createTaxCategoryMutation = useMutation({
    mutationFn: (data: { name: string; value: number; taxCode?: string }) => {
      return convexClient.mutation(createTaxCategoryRef, data);
    },
  });

  // V4.1: Navigate back to parent with created ID in search params
  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/items/${itemId}` });
  };

  const handleSubmit = async (values: TaxCategoryFormData) => {
    if (createTaxCategoryMutation.isPending) {
      return;
    }

    try {
      const taxValue =
        values.taxMethod === "automatic"
          ? 0
          : Number.parseFloat(values.value ?? "0");

      const taxCode =
        values.taxMethod === "automatic"
          ? (values.taxCode ?? undefined)
          : undefined;

      const id = await createTaxCategoryMutation.mutateAsync({
        name: values.name,
        value: taxValue,
        taxCode,
      });
      ProductEvents.taxCategoryCreated(id);
      toast.success("Tax Category added successfully");
      // V4.1: Pass created ID back via search params
      await navigate({
        to: `/menus/${menuId}/items/${itemId}`,
        search: { newTaxCategoryId: id },
      });
    } catch (error) {
      toast.error("Error adding Tax Category");
      logger.exception("tax_category_creation_failed", error);
    }
  };

  const isLoading = createTaxCategoryMutation.isPending;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent
        className="w-full max-w-120"
        noCloseButton
        noScrollPrevention
      >
        <DialogHeader className="items-center space-y-2">
          <DialogTitle>Add Tax Category</DialogTitle>
          <DialogDescription className="body">
            Create a new tax category for your items.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isLoading}
                      placeholder="Food, Beverages, Alcohol..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Calculation Method</FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          field.value === "manual"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        disabled={isLoading}
                        onClick={() => field.onChange("manual")}
                        type="button"
                      >
                        <div className="text-sm font-medium">Manual Rate</div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                          Set a fixed tax percentage
                        </div>
                      </button>
                      <button
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          field.value === "automatic"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        disabled={isLoading}
                        onClick={() => field.onChange("automatic")}
                        type="button"
                      >
                        <div className="text-sm font-medium">
                          Automatic (Stripe)
                        </div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                          Based on location
                        </div>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {taxMethod === "manual" && (
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax Rate</FormLabel>
                    <FormControl>
                      <InputGroup className="h-9 rounded-full">
                        <InputGroupInput
                          {...field}
                          className="body-sm px-3 py-2"
                          disabled={isLoading}
                          inputMode="decimal"
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d.]/g, "");
                            const parts = value.split(".");
                            if (parts.length > 2) {
                              return;
                            }
                            if (parts[0] && parts[0].length > 2) {
                              return;
                            }
                            if (parts[1] && parts[1].length > 3) {
                              return;
                            }
                            field.onChange(value);
                          }}
                          placeholder="0"
                          type="text"
                          value={field.value ?? ""}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>%</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormDescription>
                      This rate will apply to all restaurants using this tax
                      category.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {taxMethod === "automatic" && (
              <>
                <FormField
                  control={form.control}
                  name="taxCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stripe Tax Code</FormLabel>
                      <FormControl>
                        <Combobox
                          disabled={isLoading || isLoadingTaxCodes}
                          dropdownClassName="w-80"
                          filter={(value, search) => {
                            const item = taxCodeOptions.find(
                              (i) => i.value === value
                            );
                            if (!item) {
                              return 0;
                            }
                            const searchLower = search.toLowerCase();
                            if (
                              item.label.toLowerCase().includes(searchLower)
                            ) {
                              return 1;
                            }
                            if (
                              item.keywords?.some((k) =>
                                k.includes(searchLower)
                              )
                            ) {
                              return 1;
                            }
                            return 0;
                          }}
                          items={taxCodeOptions}
                          onValueChange={(value) => field.onChange(value ?? "")}
                          placeholder="Search tax codes..."
                          value={field.value ?? null}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border-info/30 bg-info/10 dark:border-info/30 dark:bg-info/10 flex gap-3 rounded-lg border p-3">
                  <Info className="text-info dark:text-info mt-0.5 size-5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-info dark:text-info/80 font-medium">
                      How automatic tax works
                    </p>
                    <p className="text-info dark:text-info/70 mt-1">
                      Stripe calculates the correct tax rate based on each
                      restaurant's location. If you have restaurants in multiple
                      states, each will automatically use the appropriate rate
                      for that jurisdiction.
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="border-warning/30 bg-warning/10 dark:border-warning/30 dark:bg-warning/10 rounded-lg border p-3">
              <p className="text-warning dark:text-warning/80 text-xs">
                <span className="font-medium">Tax Compliance Notice:</span> You
                are responsible for ensuring your tax rates are accurate and
                comply with local regulations. Plasma does not provide tax
                advice and is not liable for any tax calculation errors or
                compliance issues.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={isLoading} size="lg" type="submit">
                Add
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
